import React, {
	SetStateAction,
	useCallback,
	useEffect,
	useMemo,
	useState,
} from 'react';
import {
	Grid,
} from 'antd';
import { asyncMap } from '@shared/utils/array';
import type { AttachmentProps } from '@api/models/attachment';
import InvoiceAttachment from '@client/screens/fleet/VoyageDetailsScreen/components/InvoiceAttachment';
import LoadingIndicator from '@client/components/LoadingIndicator';
import Modal from '@client/components/Modal/Modal';
import styles from '../styles/attachments.module.css';
import showErrorNotification from '../showErrorNotification';
import getFileFromAttachment from '../getFileFromAttachment';
import useFileUpload from './useFileUpload';

const useAttachments = ({
	attachments,
	linkedAttachments,
	open,
	setOpen,
	spacerWidth = 600,
	attachmentTypeLabel,
	disabled,
}: {
	attachments: Array<Pick<AttachmentProps, 'id' | 'name' | 'type'>>;
	// Shown alongside the attachments, but can't be removed and are not part of `uploaded`
	linkedAttachments?: Array<{ label: string; attachments: Array<Pick<AttachmentProps, 'id' | 'name' | 'type'>> }>;
	open?: boolean;
	setOpen?: React.Dispatch<SetStateAction<boolean>>;
	spacerWidth?: number | string;
	attachmentTypeLabel?: string;
	disabled?: boolean;
}) => {
	const [loadingAttachments, setLoadingAttachments] = useState(false);
	const screens = Grid.useBreakpoint();

	const {
		uploaded,
		uploadProps,
		onRemoveUpload,
		onSetUploaded,
		onResetUploaded,
	} = useFileUpload({ multiple: true });

	useEffect(() => {
		if (!open) {
			onResetUploaded();
		}
	}, [onResetUploaded, open]);

	useEffect(() => {
		const updateDefaultFiles = async () => {
			setLoadingAttachments(true);

			if (
				(attachments == null || attachments.length === 0) &&
				(linkedAttachments ?? []).every((linked) => linked.attachments.length === 0)
			) {
				setLoadingAttachments(false);

				return;
			}

			try {
				const getUploadObject = async (attachment: Pick<AttachmentProps, 'id' | 'name' | 'type'>) => {
					const file: File & {
						uid?: string;
						attachmentId?: number;
						deleteable?: boolean;
					} = await getFileFromAttachment(attachment);

					file.uid = `${file.name}-${file.lastModified}`;
					file.attachmentId = attachment.id;

					return {
						file: file as File & {
							uid: string;
							attachmentId: number;
							deleteable?: boolean;
						},
						url: window.URL.createObjectURL(file),
					};
				};

				if (attachments == null) {
					return;
				}

				const files = await asyncMap(
					attachments,
					async (attachment) => {
						const uploadObj: {
							url?: any;
							deleteable?: boolean;
							file: File & {
								uid?: string;
								attachmentId?: number;
							};
						} = await getUploadObject(attachment);

						uploadObj.deleteable = true;

						return uploadObj as {
							url: any;
							deleteable?: boolean;
							file: File & {
								uid: string;
								attachmentId: number;
							};
						};
					},
				);

				const linkedFiles = await asyncMap(
					linkedAttachments ?? [],
					async (linked) => asyncMap(linked.attachments, async (attachment) => ({
						...(await getUploadObject(attachment)),
						deleteable: false,
						label: `${linked.label}: ${attachment.name}`,
					})),
				);

				onSetUploaded([...linkedFiles.flat(), ...files]);
			} catch (e) {
				showErrorNotification('Could not load attachments', e as Error);
			}

			setLoadingAttachments(false);
		};

		updateDefaultFiles();
	}, [attachments, linkedAttachments, onSetUploaded]);

	const handleModalBackgroundClick = useCallback((e: React.MouseEvent) => {
		if (e.target === e.currentTarget) {
			setOpen?.(false);
		}
	}, [setOpen]);

	const attachmentUpload = useMemo(() => (
		<div className={styles.attachmentsInner}>
			<InvoiceAttachment
				disabled={disabled}
				files={uploaded}
				uploadProps={uploadProps}
				onRemoveFile={onRemoveUpload}
				attachmentTypeLabel={attachmentTypeLabel}
			/>
		</div>
	), [attachmentTypeLabel, onRemoveUpload, uploadProps, uploaded, disabled]);

	const modal = useMemo(() => (
		<Modal
			open={open}
			wrapClassName={styles.rightSideModalWrapper}
			footer={null}
			width="100%"
			mask={false}
		>
			<div className={styles.drawerSpacerContainer}>
				<div
					style={{ width: spacerWidth, minWidth: spacerWidth }}
				/>
				<div
					className={styles.attachmentsDraggerContainer}
					data-testid="attachments-backdrop"
					onClick={handleModalBackgroundClick}
					role="presentation"
				>
					{loadingAttachments ? (
						<LoadingIndicator />
					) : (
						!screens.xs && attachmentUpload
					)}
				</div>
			</div>
		</Modal>
	), [
		open,
		spacerWidth,
		handleModalBackgroundClick,
		loadingAttachments,
		screens.xs,
		attachmentUpload,
	]);

	return {
		uploaded: (Array.isArray(uploaded) ?
			uploaded.filter((u) => u.deleteable !== false).map((u) => u.file) :
			[uploaded]
		) as File[],
		attachmentUpload,
		modal,
	};
};

export default useAttachments;
